import type { FieldDef, FieldType } from '../scene/component-types.js';

/**
 * `prism.procgen/1` documents (engine/docs/systems/generators.md, section 3)
 * and the pure helpers the Procedural panel is built on: parse / serialize
 * (unknown keys are kept), a new document, descriptor → FieldDef mapping,
 * JSON paths, diagnostics, expression tokens and the preview request of
 * section 10.1. Nothing here touches the DOM, the engine or the network.
 */

export const PROCGEN_FORMAT = 'prism.procgen/1';
export const PROCGEN_EXT = '.procgen.json';
/** Scene component key (section 11.1). */
export const PROCGEN_COMPONENT = 'procgen';

/* ------------------------------------------------------------------ */
/* Document types (mirror prism_procgen::doc; every object keeps extra keys) */

export type PropType =
  | 'number'
  | 'bool'
  | 'label'
  | 'labels'
  | 'weights'
  | 'vec2'
  | 'vec3'
  | 'range'
  | 'color'
  | 'asset'
  | 'text'
  | 'curve'
  | 'table'
  | 'noise'
  | 'positions'
  | 'shapes'
  | 'polylines';

/** Every descriptor type of section 3.3.1, in table order. */
export const PROP_TYPES: readonly PropType[] = [
  'number',
  'bool',
  'label',
  'labels',
  'weights',
  'vec2',
  'vec3',
  'range',
  'color',
  'asset',
  'text',
  'curve',
  'table',
  'noise',
  'positions',
  'shapes',
  'polylines',
];

/** One category row (section 3.3.2): `name` plus declared columns and reserved keys. */
export interface CategoryRow {
  name: string;
  color?: number[];
  [column: string]: unknown;
}

/** Property descriptor shared by params, table columns, attributes and field outputs (section 3.3). */
export interface PropDecl {
  type: PropType | (string & {});
  default?: unknown;
  range?: [number, number];
  step?: number;
  integer?: boolean;
  unit?: string;
  description?: string;
  group?: string;
  hidden?: boolean;
  categories?: unknown;
  of?: string;
  columns?: Record<string, PropInput>;
  rows?: CategoryRow[];
  points?: [number, number][];
  suffix?: string | string[];
  options?: string[];
  value?: unknown;
  seed?: number;
  [extra: string]: unknown;
}

/** A bare string is shorthand for `{ "type": "<string>" }`. */
export type PropInput = PropDecl | string;

export interface InputObject {
  path?: string;
  params?: Record<string, unknown>;
  salt?: string;
  as?: string;
  source?: string;
  file?: string;
  [extra: string]: unknown;
}
/** A path, or an object with `path` (generator) or `source` (adapter) (section 3.7). */
export type InputDecl = string | InputObject;

export interface FunctionDecl {
  args: string[];
  expr: string;
  description?: string;
  [extra: string]: unknown;
}

export type OutputKind = 'field' | 'points' | 'areas' | 'paths';
export const OUTPUT_KINDS: readonly OutputKind[] = ['field', 'points', 'areas', 'paths'];
export const FIELD_TYPES = ['number', 'bool', 'label', 'weights', 'vec2', 'vec3'] as const;

export interface PlaceDecl {
  type: string;
  [key: string]: unknown;
}

export interface SpawnDecl {
  prefab?: string | { expr: string };
  tags?: string[];
  data?: Record<string, unknown>;
  parent?: string;
  snap?: string;
  distance?: number;
  [extra: string]: unknown;
}

export interface OutputDecl {
  kind: OutputKind | (string & {});
  dims?: '2d' | '3d' | (string & {});
  description?: string;
  let?: Record<string, string>;
  salt?: string;
  color?: number[];
  preview?: Record<string, unknown>;
  type?: string;
  value?: unknown;
  cell?: number | number[];
  store?: string;
  range?: [number, number];
  unit?: string;
  default?: unknown;
  categories?: unknown;
  columns?: Record<string, PropInput>;
  combine?: string;
  base?: unknown;
  steps?: unknown[];
  remainder?: string;
  normalize?: boolean;
  fallback?: string;
  place?: PlaceDecl;
  where?: string | string[];
  chance?: unknown;
  attributes?: Record<string, PropInput>;
  transform?: Record<string, unknown>;
  footprint?: unknown;
  spacing?: unknown;
  prefer?: string;
  tile?: unknown;
  maxPerTile?: number;
  width?: unknown;
  spawn?: SpawnDecl;
  [extra: string]: unknown;
}

export interface PatchDecl {
  target: string;
  order?: number;
  description?: string;
  let?: Record<string, string>;
  value?: string;
  remove?: string;
  paint?: unknown;
  [extra: string]: unknown;
}

export interface ScriptDecl {
  path: string;
  anchor?: number | number[];
  reach?: number;
  inputReach?: number;
  reads?: string[];
  bounds?: unknown;
  maxOperations?: number;
  [extra: string]: unknown;
}

export interface ProcgenDoc {
  format: string;
  id?: string;
  name?: string;
  description?: string;
  salt?: string;
  seed?: number;
  extent?: unknown;
  default?: string;
  inputs?: Record<string, InputDecl>;
  params?: Record<string, PropInput>;
  functions?: Record<string, FunctionDecl>;
  let?: Record<string, string>;
  outputs: Record<string, OutputDecl>;
  patches?: PatchDecl[];
  script?: ScriptDecl | null;
  assets?: string[];
  editor?: Record<string, unknown>;
  [extra: string]: unknown;
}

/** Reserved words that cannot be names (section 3.2). */
export const RESERVED_WORDS: readonly string[] = [
  'true', 'false', 'none', 'and', 'or', 'not', 'in', 'it', 'parent', 'self',
  'x', 'y', 'z', 'pos', 'transform', 'id', 'found', 'dist', 'prev',
];

const NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** `[A-Za-z_][A-Za-z0-9_]*` and not reserved (PG005). */
export function isValidName(name: string): boolean {
  return NAME_RE.test(name) && !RESERVED_WORDS.includes(name);
}

/* ------------------------------------------------------------------ */
/* Parse / serialize                                                   */

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Parses a generator. Unknown keys (top level and nested) are kept as they
 * are: the engine reports them (PG003) and saving writes them back.
 * Throws on JSON that is not an object.
 */
export function parseProcgen(text: string): ProcgenDoc {
  const value: unknown = JSON.parse(text);
  if (!isRecord(value)) throw new Error('a procedural generator must be a JSON object');
  const doc = value as ProcgenDoc;
  // Only fill what the editor relies on; nothing is reordered or dropped.
  if (!isRecord(doc.outputs)) doc.outputs = {};
  if (typeof doc.format !== 'string') doc.format = doc.format === undefined ? '' : String(doc.format);
  return doc;
}

/** Two-space JSON with a final newline (the project's document style). */
export function serializeProcgen(doc: ProcgenDoc): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/** Non-throwing parse for live text. */
export function tryParseProcgen(text: string): { doc: ProcgenDoc | null; error: string | null } {
  try {
    return { doc: parseProcgen(text), error: null };
  } catch (error) {
    return { doc: null, error: error instanceof Error ? error.message : String(error) };
  }
}

/** `gen/My Biomes.procgen.json` → `my_biomes`; any text → a valid instance id. */
export function idFromName(name: string): string {
  let id = name
    .replace(/\.procgen\.json$/i, '')
    .replace(/^.*[\\/]/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!id) id = 'generator';
  if (/^[0-9]/.test(id)) id = `g_${id}`;
  if (RESERVED_WORDS.includes(id)) id = `${id}_gen`;
  return id;
}

/** 8 random hex digits (the editor's salt, section 3.1). */
export function randomSalt(): string {
  const bytes = new Uint8Array(4);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A new generator (Assets > Create > Procedural generator): format, id from
 * the name, a random 8-hex salt and one example number field.
 */
export function newProcgenDoc(name = 'New Generator'): string {
  const display = name.replace(/\.procgen\.json$/i, '').replace(/^.*[\\/]/, '').trim() || 'New Generator';
  const doc: ProcgenDoc = {
    format: PROCGEN_FORMAT,
    id: idFromName(display),
    name: display,
    description: '',
    salt: randomSalt(),
    default: 'value',
    inputs: {},
    params: {},
    outputs: {
      value: {
        kind: 'field',
        type: 'number',
        value: 'fbm(freq: 0.01)',
        description: 'Example: fractal noise in about [-1, 1]. Replace it with your own rule.',
      },
    },
  };
  return serializeProcgen(doc);
}

/** Instance name: `id`, else the file stem (section 3.1). */
export function instanceName(doc: ProcgenDoc | null, path: string): string {
  if (doc && typeof doc.id === 'string' && doc.id) return doc.id;
  return path.replace(/^.*[\\/]/, '').replace(/\.procgen\.json$/i, '');
}

/** The default output name (`default`, else the first output). */
export function defaultOutput(doc: ProcgenDoc): string | null {
  if (typeof doc.default === 'string' && doc.outputs[doc.default]) return doc.default;
  return Object.keys(doc.outputs)[0] ?? null;
}

/** The input's generator / adapter file path. */
export function inputPath(input: InputDecl): string {
  if (typeof input === 'string') return input;
  if (typeof input.path === 'string') return input.path;
  if (typeof input.file === 'string') return input.file;
  return '';
}

/** Instance name of an input (section 3.7): `as`, else the file stem. */
export function inputInstance(alias: string, input: InputDecl): string {
  if (typeof input !== 'string' && typeof input.as === 'string' && input.as) return input.as;
  const path = inputPath(input);
  if (!path) return alias;
  return path.replace(/^.*[\\/]/, '').replace(/\.(procgen|terrainstream|terrain|scene)\.json$/i, '');
}

/* ------------------------------------------------------------------ */
/* Descriptors → FieldDef                                              */

/** Normalizes the string shorthand to an object. */
export function normalizeProp(prop: PropInput): PropDecl {
  return typeof prop === 'string' ? { type: prop } : prop;
}

/** Which descriptor key holds the editable value. */
export function propValueKey(prop: PropInput): 'default' | 'points' | 'rows' {
  const p = normalizeProp(prop);
  if (p.type === 'curve') return 'points';
  if (p.type === 'table') return 'rows';
  return 'default';
}

/** The current value of a param (its default, curve points or table rows). */
export function propValue(prop: PropInput): unknown {
  const p = normalizeProp(prop);
  return p[propValueKey(p)];
}

/** A descriptor with a new value (`undefined` removes the key). */
export function withPropValue(prop: PropInput, value: unknown): PropDecl {
  const p = { ...normalizeProp(prop) };
  const key = propValueKey(p);
  if (value === undefined) delete p[key];
  else (p as Record<string, unknown>)[key] = value;
  return p;
}

/** `maxSlope` → `Max slope`. */
export function humanize(name: string): string {
  const spaced = name
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced ? spaced[0]!.toUpperCase() + spaced.slice(1) : name;
}

function stepFor(p: PropDecl): number {
  if (typeof p.step === 'number' && p.step > 0) return p.step;
  if (p.integer) return 1;
  const range = p.range;
  if (range && Number.isFinite(range[0]) && Number.isFinite(range[1]) && range[1] > range[0]) {
    const span = range[1] - range[0];
    return span >= 100 ? 1 : span >= 10 ? 0.1 : span >= 1 ? 0.01 : 0.001;
  }
  return 0.1;
}

/** The FieldEditor field type for a descriptor (section 3.3.1). */
export function propFieldType(prop: PropInput): FieldType {
  const p = normalizeProp(prop);
  switch (p.type) {
    case 'number': {
      const t: { kind: 'number'; step?: number; min?: number; max?: number; integer?: boolean } = { kind: 'number', step: stepFor(p) };
      if (p.range && Number.isFinite(p.range[0])) t.min = p.range[0];
      if (p.range && Number.isFinite(p.range[1])) t.max = p.range[1];
      if (p.integer) t.integer = true;
      return t;
    }
    case 'bool':
      return { kind: 'bool' };
    case 'label':
      return Array.isArray(p.options) && p.options.length > 0 ? { kind: 'enum', options: p.options.map(String) } : { kind: 'string', placeholder: p.of ? `a category of ${p.of}` : 'category' };
    case 'labels':
      return { kind: 'tags' };
    case 'vec3':
      return { kind: 'vec3', step: stepFor(p) };
    case 'color':
      return { kind: 'color', alpha: false };
    case 'asset': {
      const suffix = Array.isArray(p.suffix) ? p.suffix.map(String) : typeof p.suffix === 'string' && p.suffix ? p.suffix : '';
      return { kind: 'path', suffix };
    }
    case 'text':
      return { kind: 'string' };
    case 'curve':
      return { kind: 'curve', plain: true };
    // Structured values without a dedicated control: JSON (tables get TableGrid).
    case 'weights':
    case 'vec2':
    case 'range':
    case 'table':
    case 'noise':
    case 'positions':
    case 'shapes':
    case 'polylines':
    default:
      return { kind: 'json' };
  }
}

const JSON_HINTS: Record<string, string> = {
  weights: 'computed only (no literal value)',
  vec2: '[x, z]',
  range: '[lo, hi]',
  table: '{ columns, rows }',
  noise: 'terrain-stream noise nodes: [{ "type": "fbm", ... }]',
  positions: '[[x, z], ...] or [{ "id", "pos": [x, z], ...attrs }]',
  shapes: '[{ "id", "circle": [x, z, r] } | { "rect": [cx, cz, w, d], "yaw" } | { "polygon": [[x, z], ...] }]',
  polylines: '[{ "id", "points": [[x, z], ...], "width": 3 }]',
};

/** A param / column / attribute descriptor as a FieldEditor field. */
export function descriptorToFieldDef(name: string, prop: PropInput): FieldDef {
  const p = normalizeProp(prop);
  const unit = typeof p.unit === 'string' && p.unit ? ` (${p.unit})` : '';
  const hints = [p.description, JSON_HINTS[p.type], p.range ? `range ${p.range[0]} … ${p.range[1]}` : undefined].filter(
    (h): h is string => typeof h === 'string' && h.length > 0,
  );
  const def: FieldDef = { key: name, label: `${humanize(name)}${unit}`, type: propFieldType(p) };
  if (hints.length) def.hint = `${name}: ${hints.join(' · ')}`;
  return def;
}

/** Params grouped by `group` (ungrouped first), hidden ones last. */
export function groupedParams(doc: ProcgenDoc): { group: string; names: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const [name, raw] of Object.entries(doc.params ?? {})) {
    const p = normalizeProp(raw);
    const group = p.hidden ? 'Hidden' : typeof p.group === 'string' ? p.group : '';
    const list = groups.get(group) ?? [];
    list.push(name);
    groups.set(group, list);
  }
  const order = [...groups.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a === 'Hidden' ? 1 : b === 'Hidden' ? -1 : a.localeCompare(b)));
  return order.map((group) => ({ group, names: groups.get(group)! }));
}

/** A starting descriptor for a new param / column of a type. */
export function newDescriptor(type: PropType): PropDecl {
  switch (type) {
    case 'number':
      return { type, default: 0 };
    case 'bool':
      return { type, default: false };
    case 'label':
      return { type, options: ['a', 'b'], default: 'a' };
    case 'labels':
      return { type, default: [] };
    case 'vec2':
      return { type, default: [0, 0] };
    case 'vec3':
      return { type, default: [0, 0, 0] };
    case 'range':
      return { type, default: [0, 1] };
    case 'color':
      return { type, default: [0.5, 0.5, 0.5] };
    case 'asset':
      return { type, default: '', suffix: '.prefab.json' };
    case 'text':
      return { type, default: '' };
    case 'curve':
      return { type, points: [[0, 0], [1, 1]] };
    case 'table':
      return { type, columns: { radius: { type: 'number', default: 1 } }, rows: [{ name: 'first' }] };
    case 'noise':
      return { type, default: [{ type: 'fbm', frequency: 0.001, octaves: 6, amplitude: 1 }] };
    case 'positions':
    case 'shapes':
    case 'polylines':
      return { type, default: [] };
    case 'weights':
    default:
      return { type };
  }
}

/** Default cell value of a table column. */
export function columnDefault(prop: PropInput): unknown {
  const p = normalizeProp(prop);
  if (p.default !== undefined) return p.default;
  switch (p.type) {
    case 'number':
      return 0;
    case 'bool':
      return false;
    case 'labels':
      return [];
    case 'vec2':
      return [0, 0];
    case 'vec3':
    case 'color':
      return [0, 0, 0];
    default:
      return '';
  }
}

/** Coerces a typed grid cell (text input) into the column's JSON value. */
export function parseCell(prop: PropInput, text: string): unknown {
  const p = normalizeProp(prop);
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  switch (p.type) {
    case 'number': {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) return undefined;
      return p.integer ? Math.round(n) : n;
    }
    case 'bool':
      return trimmed === 'true' || trimmed === '1' || trimmed === 'yes';
    case 'labels':
      if (trimmed.startsWith('[')) {
        try {
          return JSON.parse(trimmed) as unknown;
        } catch {
          return undefined;
        }
      }
      return trimmed.split(/[,\s]+/).filter(Boolean);
    case 'label':
    case 'asset':
    case 'text':
      return trimmed;
    default:
      try {
        return JSON.parse(trimmed) as unknown;
      } catch {
        return trimmed;
      }
  }
}

/** Text shown in a grid cell. */
export function cellText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) return value.join(', ');
  return JSON.stringify(value);
}

/**
 * Category names a set ref resolves to inside this document (`"zone"` own
 * output, `"treeTypes"` table param, label `options`); null when the set
 * lives in another file.
 */
export function localSetNames(doc: ProcgenDoc, ref: string | undefined): string[] | null {
  if (!ref) return null;
  const param = doc.params?.[ref];
  if (param !== undefined) {
    const p = normalizeProp(param);
    if (p.type === 'table') return (p.rows ?? []).map((r) => String(r.name));
    if (Array.isArray(p.options)) return p.options.map(String);
  }
  const output = doc.outputs[ref];
  if (output && Array.isArray(output.categories)) return categoryNames(output.categories);
  return null;
}

/** Names of an inline category array (strings are shorthand rows). */
export function categoryNames(categories: unknown): string[] {
  if (!Array.isArray(categories)) return [];
  return categories.map((c) => (typeof c === 'string' ? c : isRecord(c) && typeof c.name === 'string' ? c.name : '')).filter(Boolean);
}

/* ------------------------------------------------------------------ */
/* JSON paths (`outputs.trees.where[0]`)                               */

export type PathSegment = string | number;

/** Splits a diagnostic JSON path into keys and indices (`a.b[0]["x y"]`). */
export function parseJsonPath(path: string): PathSegment[] {
  const out: PathSegment[] = [];
  let i = 0;
  let current = '';
  const push = () => {
    if (current) out.push(current);
    current = '';
  };
  while (i < path.length) {
    const ch = path[i]!;
    if (ch === '.') {
      push();
      i++;
    } else if (ch === '[') {
      push();
      const close = path.indexOf(']', i);
      const inner = path.slice(i + 1, close < 0 ? path.length : close).trim();
      if (/^-?\d+$/.test(inner)) out.push(Number(inner));
      else out.push(inner.replace(/^['"]|['"]$/g, ''));
      i = close < 0 ? path.length : close + 1;
    } else {
      current += ch;
      i++;
    }
  }
  push();
  return out;
}

/** Builds a path from segments (inverse of parseJsonPath for plain names). */
export function formatJsonPath(segments: readonly PathSegment[]): string {
  let out = '';
  for (const s of segments) {
    if (typeof s === 'number') out += `[${s}]`;
    else if (NAME_RE.test(s)) out += out ? `.${s}` : s;
    else out += `['${s}']`;
  }
  return out;
}

export function getAtPath(root: unknown, path: string | readonly PathSegment[]): unknown {
  const segments = typeof path === 'string' ? parseJsonPath(path) : path;
  let cur: unknown = root;
  for (const s of segments) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[s];
  }
  return cur;
}

/** Immutable set; `undefined` deletes the key (or splices the array item). */
export function setAtPath<T>(root: T, path: string | readonly PathSegment[], value: unknown): T {
  const segments = typeof path === 'string' ? parseJsonPath(path) : path;
  if (segments.length === 0) return value as T;
  const [head, ...rest] = segments as [PathSegment, ...PathSegment[]];
  const base: unknown = root;
  if (typeof head === 'number') {
    const arr = Array.isArray(base) ? base.slice() : [];
    if (rest.length === 0 && value === undefined) {
      arr.splice(head, 1);
      return arr as unknown as T;
    }
    arr[head] = setAtPath(arr[head], rest, value);
    return arr as unknown as T;
  }
  const obj: Record<string, unknown> = isRecord(base) ? { ...base } : {};
  if (rest.length === 0 && value === undefined) delete obj[head];
  else obj[head] = setAtPath(obj[head], rest, value);
  return obj as unknown as T;
}

/** Renames a key of an object while keeping its position. */
export function renameKey<V>(obj: Record<string, V>, from: string, to: string): Record<string, V> {
  if (from === to || !(from in obj) || to in obj) return obj;
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k === from ? to : k, v]));
}

/** A name not yet used by any generator-level declaration (PG006 shares one scope). */
export function freshName(doc: ProcgenDoc, base: string): string {
  const used = new Set(scopeNames(doc).map((n) => n.name));
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) if (!used.has(`${base}${i}`)) return `${base}${i}`;
}

/* ------------------------------------------------------------------ */
/* Diagnostics (section 5.12)                                          */

export type Severity = 'error' | 'warning' | 'info';

export interface DiagnosticFix {
  label: string;
  /** Byte range inside the expression text; null = the whole value. */
  span?: [number, number] | null;
  replacement: string;
}

export interface ProcgenDiagnostic {
  severity: Severity;
  code: string;
  file: string;
  path: string;
  span?: [number, number] | null;
  message: string;
  help?: string | null;
  fixes?: DiagnosticFix[];
  related?: { file: string; path: string; message: string }[];
}

function span(value: unknown): [number, number] | null {
  return Array.isArray(value) && value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number' ? [value[0], value[1]] : null;
}

/** Diagnostics from any producer (wasm, CLI JSON, `{diagnostics}` wrapper), cleaned. */
export function normalizeDiagnostics(raw: unknown): ProcgenDiagnostic[] {
  const list = Array.isArray(raw) ? raw : isRecord(raw) && Array.isArray(raw.diagnostics) ? raw.diagnostics : [];
  const out: ProcgenDiagnostic[] = [];
  for (const d of list) {
    if (!isRecord(d)) continue;
    const severity: Severity = d.severity === 'warning' ? 'warning' : d.severity === 'info' ? 'info' : 'error';
    out.push({
      severity,
      code: typeof d.code === 'string' ? d.code : '',
      file: typeof d.file === 'string' ? d.file.replace(/\\/g, '/') : '',
      path: typeof d.path === 'string' ? d.path : '',
      span: span(d.span),
      message: typeof d.message === 'string' ? d.message : String(d.message ?? ''),
      help: typeof d.help === 'string' ? d.help : null,
      fixes: Array.isArray(d.fixes)
        ? d.fixes.filter(isRecord).map((f) => ({ label: String(f.label ?? 'fix'), span: span(f.span), replacement: String(f.replacement ?? '') }))
        : [],
      related: Array.isArray(d.related)
        ? d.related.filter(isRecord).map((r) => ({ file: String(r.file ?? ''), path: String(r.path ?? ''), message: String(r.message ?? '') }))
        : [],
    });
  }
  return out;
}

/** Character index of a UTF-8 byte offset (spans are byte ranges). */
export function byteToChar(text: string, byte: number): number {
  let bytes = 0;
  let i = 0;
  while (i < text.length && bytes < byte) {
    const code = text.codePointAt(i)!;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
    i += code > 0xffff ? 2 : 1;
  }
  return i;
}

/** UTF-8 byte offset of a character index (completion `offset`). */
export function charToByte(text: string, index: number): number {
  let bytes = 0;
  let i = 0;
  while (i < text.length && i < index) {
    const code = text.codePointAt(i)!;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
    i += code > 0xffff ? 2 : 1;
  }
  return bytes;
}

/** The text after a quick fix. */
export function applyFix(text: string, fix: DiagnosticFix): string {
  if (!fix.span) return fix.replacement;
  const a = byteToChar(text, fix.span[0]);
  const b = byteToChar(text, fix.span[1]);
  return text.slice(0, a) + fix.replacement + text.slice(b);
}

/** Diagnostics of one file whose path is `path` or below it. */
export function diagnosticsAt(list: readonly ProcgenDiagnostic[], file: string, path: string, exact = true): ProcgenDiagnostic[] {
  return list.filter((d) => (!d.file || d.file === file) && (exact ? d.path === path : d.path === path || d.path.startsWith(`${path}.`) || d.path.startsWith(`${path}[`)));
}

/* ------------------------------------------------------------------ */
/* Expression tokens (syntax colouring, local completion)              */

export type TokenKind = 'number' | 'string' | 'keyword' | 'builtin' | 'ident' | 'named' | 'member' | 'op' | 'punct' | 'space' | 'error';

export interface ExprToken {
  kind: TokenKind;
  text: string;
  start: number;
  end: number;
}

export const EXPR_KEYWORDS: readonly string[] = ['true', 'false', 'none', 'and', 'or', 'not', 'in', 'it', 'parent', 'self', 'x', 'y', 'z', 'pos', 'transform', 'prev'];

/** Builtins of section 5.8 (the wasm `complete()` is authoritative; this is the offline list). */
export const EXPR_BUILTINS: readonly string[] = [
  'abs', 'sign', 'floor', 'ceil', 'trunc', 'fract', 'sqrt', 'round', 'min', 'max', 'clamp', 'saturate', 'lerp', 'invlerp', 'remap',
  'smoothstep', 'step', 'band', 'between', 'mod', 'pow', 'exp', 'ln', 'log2', 'sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2',
  'hypot', 'rad', 'deg', 'num', 'len', 'vec2', 'vec3', 'length', 'distance', 'dot', 'cross', 'normalize', 'perlin', 'fbm', 'ridged',
  'billow', 'eroded', 'worley', 'value', 'vfbm', 'warp', 'graph', 'rand', 'rand_int', 'chance', 'pick', 'hash', 'slope', 'normal',
  'grad', 'at', 'max_around', 'min_around', 'avg_around', 'dominant', 'near', 'count_near', 'overlaps', 'dist_to', 'nearest', 'inside',
  'area_at', 'case',
];

const OPS = ['??', '==', '!=', '<=', '>=', '&&', '||', '+', '-', '*', '/', '%', '^', '<', '>', '!', '?', ':', '@'];

/** Lexes an expression (section 5.2) for display; never throws. */
export function tokenizeExpr(text: string): ExprToken[] {
  const out: ExprToken[] = [];
  let i = 0;
  const push = (kind: TokenKind, start: number, end: number) => out.push({ kind, text: text.slice(start, end), start, end });
  const prevSignificant = () => {
    for (let k = out.length - 1; k >= 0; k--) if (out[k]!.kind !== 'space') return out[k]!;
    return null;
  };
  while (i < text.length) {
    const ch = text[i]!;
    const start = i;
    if (/\s/.test(ch)) {
      while (i < text.length && /\s/.test(text[i]!)) i++;
      push('space', start, i);
    } else if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(text[i + 1] ?? ''))) {
      i++;
      while (i < text.length && /[0-9.]/.test(text[i]!)) i++;
      if (/[eE]/.test(text[i] ?? '') && /[-+0-9]/.test(text[i + 1] ?? '')) {
        i += 2;
        while (i < text.length && /[0-9]/.test(text[i]!)) i++;
      }
      push('number', start, i);
    } else if (ch === "'" || ch === '"') {
      i++;
      while (i < text.length && text[i] !== ch) i++;
      const closed = i < text.length;
      if (closed) i++;
      push(closed ? 'string' : 'error', start, i);
    } else if (/[A-Za-z_]/.test(ch)) {
      while (i < text.length && /[A-Za-z0-9_]/.test(text[i]!)) i++;
      const word = text.slice(start, i);
      const prev = prevSignificant();
      let j = i;
      while (j < text.length && /\s/.test(text[j]!)) j++;
      const next = text[j];
      let kind: TokenKind;
      if (prev && (prev.text === '.' || prev.text === '@')) kind = 'member';
      else if (next === ':' && text[j + 1] !== ':' && prev && (prev.text === '(' || prev.text === ',')) kind = 'named';
      else if (EXPR_KEYWORDS.includes(word)) kind = 'keyword';
      else if (next === '(' && EXPR_BUILTINS.includes(word)) kind = 'builtin';
      else kind = 'ident';
      push(kind, start, i);
    } else {
      const op = OPS.find((o) => text.startsWith(o, i));
      if (op) {
        i += op.length;
        push('op', start, i);
      } else if ('(),[].'.includes(ch)) {
        i++;
        push('punct', start, i);
      } else {
        i++;
        push('error', start, i);
      }
    }
  }
  return out;
}

/** The identifier (with dotted head) being typed at `offset` (chars). */
export function wordAt(text: string, offset: number): { start: number; end: number; prefix: string; head: string | null } {
  let start = offset;
  while (start > 0 && /[A-Za-z0-9_]/.test(text[start - 1]!)) start--;
  let end = offset;
  while (end < text.length && /[A-Za-z0-9_]/.test(text[end]!)) end++;
  let head: string | null = null;
  if (start > 0 && text[start - 1] === '.') {
    let h = start - 1;
    while (h > 0 && /[A-Za-z0-9_.]/.test(text[h - 1]!)) h--;
    head = text.slice(h, start - 1) || null;
  }
  return { start, end, prefix: text.slice(start, offset), head };
}

/* ------------------------------------------------------------------ */
/* Names in scope                                                      */

export type ScopeKind = 'param' | 'table' | 'let' | 'function' | 'output' | 'input';

export interface ScopeName {
  name: string;
  kind: ScopeKind;
  detail: string;
}

/** Generator-level names (one scope, PG006). */
export function scopeNames(doc: ProcgenDoc): ScopeName[] {
  const out: ScopeName[] = [];
  for (const [name, raw] of Object.entries(doc.params ?? {})) {
    const p = normalizeProp(raw);
    out.push({ name, kind: p.type === 'table' ? 'table' : 'param', detail: p.type });
  }
  for (const [name, expr] of Object.entries(doc.let ?? {})) out.push({ name, kind: 'let', detail: String(expr) });
  for (const [name, fn] of Object.entries(doc.functions ?? {})) out.push({ name, kind: 'function', detail: `(${(fn.args ?? []).join(', ')})` });
  for (const [name, o] of Object.entries(doc.outputs)) out.push({ name, kind: 'output', detail: o.type ? `${o.kind} ${o.type}` : String(o.kind) });
  for (const [name, input] of Object.entries(doc.inputs ?? {})) out.push({ name, kind: 'input', detail: inputPath(input) });
  return out;
}

/** Completion item (prism_procgen::complete::Completion). */
export interface Completion {
  label: string;
  kind: string;
  detail: string;
  insert: string;
}

/** Offline completions: generator names, own categories after `.`, builtins. */
export function localCompletions(doc: ProcgenDoc | null, text: string, offset: number, outputName?: string): Completion[] {
  const w = wordAt(text, offset);
  const prefix = w.prefix.toLowerCase();
  const items: Completion[] = [];
  if (w.head && doc) {
    const head = w.head.split('.').pop() ?? '';
    const own = doc.outputs[head];
    if (own) for (const c of categoryNames(own.categories)) items.push({ label: c, kind: 'category', detail: head, insert: c });
    const param = doc.params?.[head];
    if (param !== undefined) {
      const p = normalizeProp(param);
      for (const c of Object.keys(p.columns ?? {})) items.push({ label: c, kind: 'column', detail: head, insert: c });
    }
  } else {
    if (doc) {
      for (const n of scopeNames(doc)) items.push({ label: n.name, kind: n.kind, detail: n.detail, insert: n.name });
      const out = outputName ? doc.outputs[outputName] : undefined;
      for (const [name, expr] of Object.entries(out?.let ?? {})) items.push({ label: name, kind: 'let', detail: String(expr), insert: name });
      for (const name of Object.keys(out?.attributes ?? {})) items.push({ label: name, kind: 'attribute', detail: outputName ?? '', insert: name });
    }
    for (const k of EXPR_KEYWORDS) items.push({ label: k, kind: 'keyword', detail: '', insert: k });
    for (const b of EXPR_BUILTINS) items.push({ label: b, kind: 'function', detail: 'builtin', insert: `${b}(` });
  }
  const seen = new Set<string>();
  return items.filter((c) => {
    if (seen.has(c.label) || !c.label.toLowerCase().startsWith(prefix)) return false;
    seen.add(c.label);
    return true;
  });
}

/** Normalizes `complete()` output (array, `{items}` wrapper, JSON text). */
export function normalizeCompletions(raw: unknown): Completion[] {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return [];
    }
  }
  const list = Array.isArray(value) ? value : isRecord(value) && Array.isArray(value.items) ? value.items : [];
  return list.filter(isRecord).map((c) => ({
    label: String(c.label ?? ''),
    kind: String(c.kind ?? ''),
    detail: String(c.detail ?? ''),
    insert: String(c.insert ?? c.label ?? ''),
  }));
}

/* ------------------------------------------------------------------ */
/* Expression slots of a document                                      */

/** An editable expression inside the document (JSON path + text). */
export interface ExprSlot {
  path: string;
  label: string;
  text: string;
}

const EXPR_OUTPUT_KEYS = ['value', 'chance', 'footprint', 'prefer', 'width'] as const;

/** Every string expression slot of one output (for the inspector). */
export function outputExprSlots(name: string, output: OutputDecl): ExprSlot[] {
  const base = `outputs.${name}`;
  const slots: ExprSlot[] = [];
  for (const key of EXPR_OUTPUT_KEYS) {
    const v = output[key];
    if (typeof v === 'string' && !(key === 'value' && v === 'script')) slots.push({ path: `${base}.${key}`, label: key, text: v });
  }
  if (typeof output.where === 'string') slots.push({ path: `${base}.where`, label: 'where', text: output.where });
  else if (Array.isArray(output.where)) output.where.forEach((w, i) => slots.push({ path: `${base}.where[${i}]`, label: `where[${i}]`, text: String(w) }));
  if (typeof output.spacing === 'string' && !['auto', 'none'].includes(output.spacing)) slots.push({ path: `${base}.spacing`, label: 'spacing', text: output.spacing });
  return slots;
}

/* ------------------------------------------------------------------ */
/* Preview request / result (section 10)                               */

export type Slice = 'xz' | 'xy' | 'zy';

export interface PreviewRequest {
  graph: string[];
  seed: number;
  params?: Record<string, Record<string, unknown>>;
  show?: string;
  scope?: string;
  rect?: [number, number, number, number];
  center?: [number, number];
  extent?: number;
  size?: [number, number];
  y?: number;
  slice?: Slice;
  shade?: string;
  ramp?: string;
  weights?: string;
  overlays?: string[];
  overlayWhere?: Record<string, string>;
  maxPoints?: number;
  maxSamples?: number;
  cursor?: number;
  probe?: [number, number, number];
  lod?: 'auto' | 'exact';
  host?: string;
}

export interface PreviewLegendEntry {
  name: string;
  color: number[];
  cover?: number;
}

export interface PreviewPoint {
  ref: string;
  id: string;
  x: number;
  y: number;
  z: number;
  label?: string | null;
  color?: number[] | null;
  attrs?: Record<string, unknown>;
}

export interface PreviewArea {
  ref: string;
  id: string;
  circle?: [number, number, number];
  rect?: number[];
  yaw?: number;
  polygon?: [number, number][];
  attrs?: Record<string, unknown>;
  [extra: string]: unknown;
}

export interface PreviewPath {
  ref: string;
  id: string;
  points: [number, number][];
  width?: number;
  attrs?: Record<string, unknown>;
}

export interface PreviewProbe {
  value?: unknown;
  label?: string | null;
  weights?: Record<string, number>;
  [extra: string]: unknown;
}

export interface PreviewStats {
  samples?: number;
  tasks?: number;
  cacheHits?: number;
  points?: number;
  truncated?: boolean;
  noneReplaced?: number;
  clamped?: number;
  divZero?: number;
  costPerLane?: number;
  pendingScripts?: number;
  [extra: string]: unknown;
}

/** PreviewResult (section 10.2). Every key is optional: producers differ in age. */
export interface PreviewResult {
  ok?: boolean;
  kind?: string;
  size?: [number, number];
  rect?: [number, number, number, number];
  rgba?: string;
  rows?: [number, number];
  min?: number | null;
  max?: number | null;
  unit?: string | null;
  legend?: PreviewLegendEntry[];
  points?: PreviewPoint[];
  areas?: PreviewArea[];
  paths?: PreviewPath[];
  probe?: PreviewProbe | null;
  stats?: PreviewStats;
  approximate?: boolean;
  done?: boolean;
  cursor?: number;
  missing?: string[];
  diagnostics?: unknown[];
  error?: string;
  [extra: string]: unknown;
}

/** Panel preview settings (persisted in `editor.preview` of the open generator). */
export interface PreviewSettings {
  /** Output reference or expression; empty = the default output. */
  show: string;
  scope: string | null;
  rect: [number, number, number, number];
  size: [number, number];
  seed: number;
  y: number;
  slice: Slice;
  shade: string;
  ramp: string;
  weights: 'blend' | 'dominant';
  overlays: string[];
  overlayWhere: Record<string, string>;
  maxPoints: number;
  maxSamples: number;
  lod: 'auto' | 'exact';
}

export const MAX_PREVIEW_SIZE = 2048;

export const DEFAULT_PREVIEW: PreviewSettings = {
  show: '',
  scope: null,
  rect: [-512, -512, 512, 512],
  size: [256, 256],
  seed: 1,
  y: 0,
  slice: 'xz',
  shade: '',
  ramp: 'auto',
  weights: 'blend',
  overlays: [],
  overlayWhere: {},
  maxPoints: 20000,
  maxSamples: 65536,
  lod: 'auto',
};

function finite(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Settings from a document's `editor.preview` over the defaults. */
export function previewSettingsFor(doc: ProcgenDoc | null): PreviewSettings {
  const stored: Record<string, unknown> = doc && isRecord(doc.editor) && isRecord(doc.editor.preview) ? doc.editor.preview : {};
  const s: PreviewSettings = { ...DEFAULT_PREVIEW, overlays: [], overlayWhere: {} };
  if (typeof stored.show === 'string') s.show = stored.show;
  if (Array.isArray(stored.rect) && stored.rect.length === 4) s.rect = stored.rect.map((v, i) => finite(v, DEFAULT_PREVIEW.rect[i]!)) as PreviewSettings['rect'];
  if (Array.isArray(stored.size) && stored.size.length === 2) s.size = stored.size.map((v, i) => finite(v, DEFAULT_PREVIEW.size[i]!)) as PreviewSettings['size'];
  if (typeof stored.seed === 'number') s.seed = stored.seed;
  if (typeof stored.y === 'number') s.y = stored.y;
  if (stored.slice === 'xy' || stored.slice === 'zy' || stored.slice === 'xz') s.slice = stored.slice;
  if (typeof stored.shade === 'string') s.shade = stored.shade;
  if (typeof stored.ramp === 'string') s.ramp = stored.ramp;
  if (stored.weights === 'dominant' || stored.weights === 'blend') s.weights = stored.weights;
  if (Array.isArray(stored.overlays)) s.overlays = stored.overlays.filter((o): o is string => typeof o === 'string');
  if (isRecord(stored.overlayWhere)) {
    for (const [k, v] of Object.entries(stored.overlayWhere)) if (typeof v === 'string') s.overlayWhere[k] = v;
  }
  return s;
}

/** Global output references of this generator (`instance.output`). */
export function outputRefs(doc: ProcgenDoc, instance: string, kinds?: readonly string[]): string[] {
  return Object.entries(doc.outputs)
    .filter(([, o]) => !kinds || kinds.includes(String(o.kind)))
    .map(([name]) => `${instance}.${name}`);
}

function normalizePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\.?\/+/, '');
}

/**
 * The request of section 10.1. Absent / empty settings are left out (the
 * engine defaults them); sizes are clamped to 1..2048, the rect is ordered,
 * overlays are deduplicated and filters only kept for shown overlays.
 */
export function buildPreviewRequest(options: {
  graph: readonly string[];
  settings: PreviewSettings;
  /** Fallback `show` when the setting is empty (the default output reference). */
  defaultShow?: string | null;
  params?: Record<string, Record<string, unknown>>;
  cursor?: number;
  probe?: [number, number, number] | null;
  host?: string | null;
}): PreviewRequest {
  const s = options.settings;
  const graph = [...new Set(options.graph.map(normalizePath).filter(Boolean))];
  const req: PreviewRequest = { graph, seed: Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.round(finite(s.seed, 1)))) };
  if (options.params && Object.keys(options.params).length) req.params = options.params;
  const show = (s.show.trim() || options.defaultShow || '').trim();
  if (show) req.show = show;
  if (s.scope) req.scope = s.scope;
  const [x0, z0, x1, z1] = s.rect;
  req.rect = [Math.min(x0, x1), Math.min(z0, z1), Math.max(x0, x1), Math.max(z0, z1)];
  const dim = (v: number) => Math.max(1, Math.min(MAX_PREVIEW_SIZE, Math.round(finite(v, 256))));
  req.size = [dim(s.size[0]), dim(s.size[1])];
  if (s.y) req.y = s.y;
  if (s.slice !== 'xz') req.slice = s.slice;
  if (s.shade.trim()) req.shade = s.shade.trim();
  if (s.ramp) req.ramp = s.ramp;
  if (s.weights) req.weights = s.weights;
  const overlays = [...new Set(s.overlays.map((o) => o.trim()).filter(Boolean))];
  if (overlays.length) {
    req.overlays = overlays;
    const where: Record<string, string> = {};
    for (const o of overlays) {
      const f = s.overlayWhere[o]?.trim();
      if (f) where[o] = f;
    }
    if (Object.keys(where).length) req.overlayWhere = where;
  }
  if (s.maxPoints > 0) req.maxPoints = Math.round(s.maxPoints);
  if (s.maxSamples > 0) req.maxSamples = Math.round(s.maxSamples);
  if (options.cursor) req.cursor = Math.max(0, Math.round(options.cursor));
  if (options.probe) req.probe = options.probe;
  req.lod = s.lod;
  if (options.host) req.host = options.host;
  return req;
}

/** Parses a preview response (JSON text or object). */
export function parsePreviewResult(raw: unknown): PreviewResult {
  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw) as unknown;
    } catch {
      return { ok: false, error: `bad preview response: ${raw.slice(0, 200)}` };
    }
  }
  if (!isRecord(value)) return { ok: false, error: 'empty preview response' };
  return value as PreviewResult;
}

/* ------------------------------------------------------------------ */
/* Dependency layers (section 6.5)                                     */

export interface DepNode {
  id: string;
  label: string;
  /** `own` = an output of this generator; `input` = something read through an input alias. */
  group: 'own' | 'input';
  stage: number;
  error?: boolean;
  cycle?: boolean;
}

export interface DepGraph {
  nodes: DepNode[];
  edges: { from: string; to: string }[];
  /** Whether the layers came from the engine's `describe` JSON. */
  fromEngine: boolean;
}

/** Strings anywhere under a value (expression slots). */
function strings(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) for (const v of value) strings(v, out);
  else if (isRecord(value)) for (const v of Object.values(value)) strings(v, out);
  return out;
}

/** References `alias.member` / own output names read by one output. */
function readsOf(doc: ProcgenDoc, outputName: string, output: OutputDecl): string[] {
  const aliases = new Set(Object.keys(doc.inputs ?? {}));
  const own = new Set(Object.keys(doc.outputs));
  const lets = { ...(doc.let ?? {}) };
  const found = new Set<string>();
  const visit = (text: string, depth: number) => {
    const tokens = tokenizeExpr(text).filter((t) => t.kind !== 'space');
    tokens.forEach((t, i) => {
      if (t.kind !== 'ident' && t.kind !== 'builtin') return;
      if (aliases.has(t.text)) {
        const dot = tokens[i + 1];
        const member = tokens[i + 2];
        found.add(dot?.text === '.' && member && member.kind === 'member' ? `${t.text}.${member.text}` : t.text);
      } else if (own.has(t.text) && t.text !== outputName) found.add(t.text);
      else if (depth < 8 && lets[t.text] !== undefined) visit(String(lets[t.text]), depth + 1);
    });
  };
  for (const text of strings({ ...output, kind: undefined, type: undefined, description: undefined, dims: undefined })) visit(text, 0);
  return [...found];
}

/** Layers by longest path; strongly connected nodes are flagged as cycles. */
function layer(ids: string[], edges: { from: string; to: string }[]): { stage: Map<string, number>; cyclic: Set<string> } {
  const incoming = new Map<string, string[]>();
  for (const id of ids) incoming.set(id, []);
  for (const e of edges) incoming.get(e.to)?.push(e.from);
  const stage = new Map<string, number>();
  const cyclic = new Set<string>();
  const visiting = new Set<string>();
  const visit = (id: string, trail: string[]): number => {
    const known = stage.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) {
      for (const t of trail.slice(trail.indexOf(id))) cyclic.add(t);
      return 0;
    }
    visiting.add(id);
    let s = 0;
    for (const from of incoming.get(id) ?? []) s = Math.max(s, visit(from, [...trail, id]) + 1);
    visiting.delete(id);
    stage.set(id, s);
    return s;
  };
  for (const id of ids) visit(id, []);
  return { stage, cyclic };
}

/** The dependency view computed from one document's inputs and expressions. */
export function computeDepGraph(doc: ProcgenDoc, instance: string, diagnostics: readonly ProcgenDiagnostic[] = []): DepGraph {
  const nodes = new Map<string, DepNode>();
  const edges: { from: string; to: string }[] = [];
  for (const name of Object.keys(doc.outputs)) nodes.set(`${instance}.${name}`, { id: `${instance}.${name}`, label: `${instance}.${name}`, group: 'own', stage: 0 });
  for (const [name, output] of Object.entries(doc.outputs)) {
    for (const read of readsOf(doc, name, output)) {
      let from: string;
      if (doc.outputs[read]) from = `${instance}.${read}`;
      else {
        const [alias, member] = read.split('.');
        const input = doc.inputs?.[alias!];
        const inst = input !== undefined ? inputInstance(alias!, input) : alias!;
        from = member ? `${inst}.${member}` : inst;
        if (!nodes.has(from)) nodes.set(from, { id: from, label: from, group: 'input', stage: 0 });
      }
      edges.push({ from, to: `${instance}.${name}` });
    }
  }
  for (const p of doc.patches ?? []) {
    if (typeof p.target !== 'string' || !p.target) continue;
    const [alias, member] = p.target.split('.');
    const input = doc.inputs?.[alias!];
    const target = input !== undefined && member ? `${inputInstance(alias!, input)}.${member}` : doc.outputs[alias!] ? `${instance}.${alias}` : p.target;
    if (!nodes.has(target)) nodes.set(target, { id: target, label: target, group: 'input', stage: 0 });
  }
  const ids = [...nodes.keys()];
  const { stage, cyclic } = layer(ids, edges);
  for (const n of nodes.values()) {
    n.stage = stage.get(n.id) ?? 0;
    if (cyclic.has(n.id)) n.cycle = true;
    if (n.group === 'own') {
      const out = n.id.slice(instance.length + 1);
      if (diagnostics.some((d) => d.severity === 'error' && (d.path === `outputs.${out}` || d.path.startsWith(`outputs.${out}.`) || d.path.startsWith(`outputs.${out}[`))))
        n.error = true;
    }
  }
  return { nodes: [...nodes.values()], edges, fromEngine: false };
}

/**
 * The engine's `Graph::describe()` JSON (CLI `procgen graph --json`) as a
 * DepGraph. Accepts `{ nodes: [{instance, output, stage, reads|inputs}] }`
 * or `{ stages: [["a.b", ...], ...] }`; null when neither is present.
 */
export function depGraphFromDescribe(raw: unknown): DepGraph | null {
  if (!isRecord(raw)) return null;
  const nodes: DepNode[] = [];
  const edges: { from: string; to: string }[] = [];
  const nodeId = (n: Record<string, unknown>): string => {
    const name = typeof n.name === 'string' ? n.name : typeof n.instance === 'string' && typeof n.output === 'string' ? `${n.instance}.${n.output}` : String(n.id ?? '');
    const variant = typeof n.variant === 'string' && n.variant && n.variant.toLowerCase() !== 'own' ? `@${n.variant.toLowerCase()}` : '';
    return name + (name.includes('@') ? '' : variant);
  };
  if (Array.isArray(raw.nodes) && raw.nodes.some(isRecord)) {
    const list = raw.nodes.filter(isRecord);
    for (const n of list) {
      const id = nodeId(n);
      if (!id) continue;
      nodes.push({ id, label: id, group: 'own', stage: typeof n.stage === 'number' ? n.stage : 0, ...(n.cycle === true ? { cycle: true } : {}), ...(n.error === true ? { error: true } : {}) });
      const reads = Array.isArray(n.reads) ? n.reads : Array.isArray(n.inputs) ? n.inputs : [];
      for (const r of reads) {
        const from = typeof r === 'string' ? r : isRecord(r) ? nodeId(isRecord(r.node) ? r.node : r) : '';
        if (from) edges.push({ from, to: id });
      }
    }
  } else if (Array.isArray(raw.stages)) {
    raw.stages.forEach((stage, s) => {
      if (!Array.isArray(stage)) return;
      for (const n of stage) {
        const id = typeof n === 'string' ? n : isRecord(n) ? nodeId(n) : '';
        if (id) nodes.push({ id, label: id, group: 'own', stage: s });
      }
    });
  } else return null;
  const known = new Set(nodes.map((n) => n.id));
  return { nodes, edges: edges.filter((e) => known.has(e.from) && known.has(e.to)), fromEngine: true };
}
